import type { UiForm, UiTypes } from "various-ui";

import { codonDict, codons } from "./options";

export const useComposable = (emit: { (event: "redraw", data: any): void }) => {
    //* 获取I18n
    const i18n = useI18n();

    //* 响应式变量
    const refs = {
        form: ref<InstanceType<typeof UiForm>>(),

        dictionaries: ref<any>({}), //? 字典
        candidate: ref<UiTypes.candidate[]>([]), //? 候选项
        original: ref<any>({}), //? 原始数据
    };

    //* 表单数据
    const data = reactive({
        coordinate: "",
        codon: "",
        oaa: "",
        naa: "",
    });

    //* 表单校验规则
    const rules: { [name: string]: UiTypes.verifyRule[] } = {
        oaa: [
            {
                trigger: "change",
                verify: (data: any) => {
                    if (data.oaa) return { verify: true, message: "" };
                    else {
                        return { verify: false, message: "Please give us a original amino acid" };
                    }
                },
            },
        ],
        naa: [
            {
                trigger: "change",
                verify: (data: any) => {
                    if (!/^([A|R|N|D|C|Q|E|G|H|I|L|K|M|F|P|S|T|W|Y|V|*]*)$/.test(data.naa)) {
                        return { verify: false, message: "only alphabet 'ARNDCQEGHILKMFPSTWYV*' are allowed" };
                    } else {
                        return { verify: true, message: "" };
                    }
                },
            },
        ],
        coordinate: [
            {
                trigger: "verify",
                verify: (data: any) => {
                    const coordinate = Number(data.coordinate);
                    if (!coordinate || coordinate < 1 || coordinate > refs.dictionaries.value.protein.length) {
                        return { verify: false, message: "Please input the position information" };
                    } else {
                        return { verify: true, message: "" };
                    }
                },
            },
        ],
    };

    //* 函数列表
    const methods = {
        //* 初始化函数
        init: function (origin: any, dictionaries: any) {
            //* 重置表单
            refs.form.value?.reset();

            //* 数据初始化
            data.naa = "";
            data.oaa = "";
            data.codon = "";
            data.coordinate = "";

            //* 数据缓存
            refs.original.value = origin || {};
            refs.dictionaries.value = dictionaries || {};
        },

        //* 切换预测位点
        changePos: () => {
            const handler = (valid: boolean) => {
                const coordinate = Number(data.coordinate) || 0;
                const protein = refs.dictionaries.value.protein[coordinate - 1];
                if (valid && protein) {
                    data.oaa = protein.value;
                    refs.form.value?.validator(() => {}, ["oaa"]);
                    if (data.codon && /^([a|A|t|T|c|C|g|G]*)$/.test(data.codon)) {
                        emit("redraw", { start: protein.start, end: protein.end, ref: protein.value, alt: data.codon });
                    } else {
                        emit("redraw", { start: protein.start, end: protein.end, ref: protein.value });
                    }
                } else {
                    data.oaa = "";
                }
            };

            refs.form.value?.validator(handler, ["coordinate"]);
        },

        //* 切换Naa
        changeNaa: function () {
            const handler = (valid: boolean) => {
                if (!valid) return;

                const coordinate = Number(data.coordinate) || 0;
                const protein = refs.dictionaries.value.protein[coordinate - 1];
                const codon = codons[data.naa] || {};

                refs.candidate.value = [];
                (codon.codon || []).forEach((val) => {
                    refs.candidate.value.push({ label: val, value: val });
                });

                if (data.naa == "") {
                    protein && emit("redraw", { start: protein.start, end: protein.end, ref: protein.value });
                } else if (refs.candidate.value.length > 0) {
                    let codonNumber = 0;
                    let codon = refs.candidate.value[0].value;
                    if (data.oaa) {
                        refs.candidate.value.forEach((c) => {
                            let number = 0;
                            if (c.value == data.oaa) return;
                            if (c.value.charAt(0) == data.oaa.charAt(0)) number++;
                            if (c.value.charAt(1) == data.oaa.charAt(1)) number++;
                            if (c.value.charAt(2) == data.oaa.charAt(2)) number++;
                            if (number > codonNumber) {
                                codon = c.value;
                                codonNumber = number;
                            }
                        });
                    }

                    data.codon = codon;
                    protein && emit("redraw", { start: protein.start, end: protein.end, ref: protein.value, alt: data.codon });
                }
            };

            refs.form.value?.validator(handler, ["naa"]);
        },

        //* 切换Codon
        changeCodon: function () {
            const coordinate = Number(data.coordinate) || 0;
            const protein = refs.dictionaries.value.protein[coordinate - 1];
            const naa = codonDict[data.codon];

            refs.candidate.value = [];
            if (naa) {
                data.naa = naa;
                const codon = codons[naa] || {};
                (codon.codon || []).forEach((val) => {
                    refs.candidate.value.push({ label: val, value: val });
                });
            } else {
                data.naa = "";
            }

            data.codon = data.codon.toUpperCase();
            refs.form.value?.validator(() => {}, ["naa"]);
            protein && emit("redraw", { start: protein.start, end: protein.end, ref: protein.value, alt: data.codon });
        },

        //* 表单提交事件
        submit: function (callBack: (data: any) => void) {
            refs.form.value?.validator((valid: boolean) => {
                if (valid) {
                    const coordinate = Number(data.coordinate) || 0;
                    const protein = refs.dictionaries.value.protein[coordinate - 1];
                    callBack && callBack({ alt: data.codon, ref: data.oaa, gene_pos: protein.start });
                }
            });
        },
    };

    //* 计算属性
    const computeds = {
        oaa: computed(() => {
            if (data.coordinate && data.oaa) {
                const key = codonDict[data.oaa];
                return `${key} (${codons[key].shortnanme}) : ${data.oaa}`;
            }

            return "";
        }),
    };

    return { refs, data, rules, methods, computeds };
};
